
import * as React from "react"

import { cn } from "@/lib/utils"

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, onChange, onInput, ...props }, ref) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    
    // Auto-resize function
    const autoResize = React.useCallback((textarea: HTMLTextAreaElement) => {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(80, textarea.scrollHeight)}px`;
    }, []);

    // Handle change events
    const handleChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      autoResize(e.target);
      onChange?.(e);
    }, [onChange, autoResize]);

    // Handle input events  
    const handleInput = React.useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
      autoResize(e.target as HTMLTextAreaElement);
      onInput?.(e);
    }, [onInput, autoResize]);

    // Auto-resize on mount if there's initial content
    React.useEffect(() => {
      const textarea = textareaRef.current;
      if (textarea && (props.value || props.defaultValue)) {
        autoResize(textarea);
      }
    }, [props.value, props.defaultValue, autoResize]);

    return (
      <textarea
        className={cn(
          "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        ref={(node) => {
          textareaRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        onChange={handleChange}
        onInput={handleInput}
        {...props}
      />
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
